import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft, faUsers, faCalendarDays, faChild, faUserTie,
  faSpinner, faExclamationTriangle, faSearch, faFilter,
  faEdit, faTrash, faEye, faPlus, faDownload, faGear,
  faChartLine, faDatabase, faShield, faBell, faRefresh,
  faTimes, faSave, faInfoCircle, faImage, faRoute, faMapMarkerAlt,
  faUser, faHeartPulse, faLocationDot, faGaugeHigh, faClock, faMoneyBill, faHouse, faUserGroup, faCheck
} from '@fortawesome/free-solid-svg-icons';
import { dashboardService, AdminDashboardStats } from '../services/dashboardService';
import { userService, eventService, registrationService } from '../services/database';
import { User, Event as SupabaseEvent, Registration, Minor, EventDistance, PaymentConfig, supabase } from '../lib/supabase';
import { storageService } from '../services/storageService';

interface AdminUser extends User {
  registrationCount: number
  lastActivity: string
  status: 'active' | 'inactive' | 'suspended'
  role: 'admin'
  permissions: string[]
}

interface DistanceOption {
  id: string;
  name: string;
  length: string;
  price: string;
  description: string;
  ageRestriction: string;
  difficulty: string;
}

interface CreateEventForm {
  title: string
  description: string
  date: string
  location: string
  max_participants: number
  registration_deadline: string
  requirements: string
  amenities: string[]
  organizer: string
  difficulty: string
  entry_fee_from: string
  image_url: string
  route_url: string
  goreg: 'yes' | 'no'
  distances: DistanceOption[]
}

interface Event {
  id: string;
  title: string;
  description: string;
  date: string;
  location: string;
  max_participants: number;
  current_participants: number;
  registration_deadline: string;
  created_at: string;
  updated_at: string;
  goreg: 'yes' | 'no';
  requirements?: string;
  amenities?: string[];
  organizer?: string;
  difficulty?: string;
  entry_fee_from?: string;
  image_url?: string;
  route_url?: string;
  organizer_id?: string;
  payment_gateway?: 'payfast' | 'paypal' | 'stripe' | 'manual';
  payment_config?: PaymentConfig;
  distances?: EventDistance[];
}

const AdminManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'events' | 'system'>('overview');
  const [adminStats, setAdminStats] = useState<AdminDashboardStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);

  // Add User/Coordinator state
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [addUserType, setAddUserType] = useState<'user' | 'coordinator'>('user');
  const [addUserLoading, setAddUserLoading] = useState(false);
  const [addUserForm, setAddUserForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'male' as 'male' | 'female',
    csaLicence: '',
    emergencyName: '',
    emergencyPhone: '',
    relationship: '',
    allergies: '',
    medicalConditions: '',
    // Coordinator-specific fields
    paymentGateway: 'payfast' as 'payfast' | 'manual' | 'both',
    payfastMerchantId: '',
    payfastMerchantKey: '',
    payfastPassphrase: '',
    payfastSandbox: true,
    bankName: '',
    accountHolder: '',
    accountNumber: '',
    branchCode: '',
    accountType: 'Cheque',
    paymentInstructions: ''
  });

  // Event editing state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  
  // Participant management state
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [allParticipants, setAllParticipants] = useState<(Registration & { event_title: string })[]>([]);
  const [selectedEventForParticipants, setSelectedEventForParticipants] = useState<Event | null>(null);
  const [participantsLoading, setParticipantsLoading] = useState(false);
  
  const [editForm, setEditForm] = useState<CreateEventForm>({
    title: '',
    description: '',
    date: '',
    location: '',
    max_participants: 100,
    registration_deadline: '',
    requirements: '',
    amenities: [],
    organizer: '',
    difficulty: 'Intermediate',
    entry_fee_from: '',
    image_url: '',
    route_url: '',
    goreg: 'yes',
    distances: []
  });

  const initializeEditForm = (event: SupabaseEvent) => {
    setEditingEvent(event);
    setShowEditModal(true);
    console.log('Set editingEvent:', event);
    console.log('Set showEditModal: true');

    // Convert date to the correct format for input[type="datetime-local"]
    const eventDate = new Date(event.date);
    const formattedDate = eventDate.toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm format
    const deadlineDate = new Date(event.registration_deadline);
    const formattedDeadline = deadlineDate.toISOString().slice(0, 16);

    // Set initial form state without distances
    setEditForm({
      title: event.title || '',
      description: event.description || '',
      date: formattedDate,
      location: event.location || '',
      max_participants: event.max_participants || 100,
      registration_deadline: formattedDeadline,
      requirements: event.requirements || '',
      amenities: event.amenities || [],
      organizer: event.organizer_id || '',
      difficulty: event.difficulty || 'Intermediate',
      entry_fee_from: event.entry_fee_from || '',
      image_url: event.image_url || '',
      route_url: event.route_url || '',
      goreg: event.goreg || 'yes',
      distances: []
    });
    setImagePreview(event.image_url || '');

    // Fetch distances in the background
    eventService.getEventDistances(event.id)
      .then(distances => {
        const eventDistances: DistanceOption[] = distances.map(d => ({
          id: d.id,
          name: d.name,
          length: d.length,
          price: d.price.toString(),
          description: d.description || '',
          ageRestriction: d.age_restriction || '12+',
          difficulty: d.difficulty || 'Intermediate'
        }));
        setEditForm(prev => ({ ...prev, distances: eventDistances }));
      })
      .catch(err => {
        console.error('Error loading event distances:', err);
        setEditForm(prev => ({
          ...prev,
          distances: [
            {
              id: 'default-1',
              name: 'Standard Course',
              length: '25 km',
              price: '250',
              description: 'Main trail route',
              ageRestriction: '12+',
              difficulty: 'Intermediate'
            }
          ]
        }));
      });
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingEvent(null);
    setEditForm({
      title: '',
      description: '',
      date: '',
      location: '',
      max_participants: 100,
      registration_deadline: '',
      requirements: '',
      amenities: [],
      organizer: '',
      difficulty: 'Intermediate',
      entry_fee_from: '',
      image_url: '',
      route_url: '',
      goreg: 'yes',
      distances: []
    });
    setImagePreview('');
    setError(null);
  };

  const handleUpdateEvent = async () => {
    if (!editingEvent) return;

    try {
      setEditLoading(true);
      setError(null);

      // Upload new image if selected
      let imageUrl = editingEvent.image_url;
      if (imageFile) {
        try {
          imageUrl = await storageService.uploadEventImage(imageFile);
          console.log('Image uploaded successfully:', imageUrl);
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          setError(
            uploadError instanceof Error
              ? uploadError.message
              : 'Failed to upload image. Please try again.'
          );
          return;
        }
      }

      // Update event in database
      const { distances, ...eventData } = editForm;
      const updatedEvent = await eventService.updateEvent(editingEvent.id, {
        ...eventData,
        image_url: imageUrl,
        amenities: editForm.amenities.length > 0 ? editForm.amenities : undefined
      });

      // Debug log: distances to be updated
      console.log('Updating event distances for eventId:', editingEvent.id, 'with distances:', editForm.distances);

      // Update event distances
      if (editForm.distances && editForm.distances.length > 0) {
        const distanceData = editForm.distances.map(distance => ({
          name: distance.name,
          length: distance.length,
          price: parseFloat(distance.price) || 0,
          description: distance.description,
          age_restriction: distance.ageRestriction,
          difficulty: distance.difficulty
        }));
        try {
          const result = await eventService.updateEventDistances(editingEvent.id, distanceData);
          console.log('Result from updateEventDistances:', result);
        } catch (err) {
          console.error('Error updating event distances:', err);
        }
      }

      // Reload events list
      await loadAdminData();
      handleCloseEditModal();
      console.log('Event updated successfully:', updatedEvent);
    } catch (err) {
      console.error('Error updating event:', err);
      setError('Failed to update event');
    } finally {
      setEditLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load admin statistics
      const stats = await dashboardService.getAdminDashboardStats();
      setAdminStats(stats);

      // Load users
      try {
        const { data: allUsers, error } = await supabase
          .from('users')
          .select('*')
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading users:', error);
          setUsers([]);
        } else {
          const adminUsers: AdminUser[] = allUsers.map(user => ({
            ...user,
            registrationCount: 0,
            lastActivity: user.created_at,
            status: 'active' as const
          }));
          
          setUsers(adminUsers);
          console.log(`Loaded ${adminUsers.length} real users from database`);
        }
      } catch (err) {
        console.error('Error loading users:', err);
        setUsers([]);
      }

      // Load events
      const allEvents = await eventService.getAllEvents();
      setEvents(allEvents);

    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return formatDate(dateString);
  };

  const filteredUsers = users.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredEvents = events.filter(event =>
    event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    event.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Utility functions
  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const determineMTBCategory = (dateOfBirth: string, gender: 'male' | 'female') => {
    const age = calculateAge(dateOfBirth);
    
    // Category determination logic
    if (age < 6) return { category: 'Too Young', valid: false };
    if (age >= 6 && age <= 8) return { category: `${gender === 'male' ? 'Boys' : 'Girls'} 6-8`, valid: true };
    if (age >= 9 && age <= 10) return { category: `${gender === 'male' ? 'Boys' : 'Girls'} 9-10`, valid: true };
    if (age >= 11 && age <= 12) return { category: `${gender === 'male' ? 'Boys' : 'Girls'} 11-12`, valid: true };
    if (age >= 13 && age <= 14) return { category: `${gender === 'male' ? 'Boys' : 'Girls'} 13-14`, valid: true };
    if (age >= 15 && age <= 16) return { category: `${gender === 'male' ? 'Boys' : 'Girls'} 15-16`, valid: true };
    if (age >= 17 && age <= 18) return { category: `Junior ${gender === 'male' ? 'Men' : 'Women'}`, valid: true };
    if (age >= 19 && age <= 29) return { category: `${gender === 'male' ? 'Men' : 'Women'} 19-29`, valid: true };
    if (age >= 30 && age <= 39) return { category: `${gender === 'male' ? 'Men' : 'Women'} 30-39`, valid: true };
    if (age >= 40 && age <= 49) return { category: `${gender === 'male' ? 'Men' : 'Women'} 40-49`, valid: true };
    if (age >= 50 && age <= 59) return { category: `${gender === 'male' ? 'Men' : 'Women'} 50-59`, valid: true };
    if (age >= 60) return { category: `${gender === 'male' ? 'Men' : 'Women'} 60+`, valid: true };
    
    return { category: 'Unknown', valid: false };
  };

  const commonAmenities = [
    'Parking', 'Toilets', 'Water Points', 'Medical Support', 'Bike Wash',
    'Food Vendors', 'Timing Chips', 'Photography', 'Live Tracking', 'Prizes'
  ];

  // Handler Functions
  const handleCloseAddUserModal = () => {
    setShowAddUserModal(false);
    setAddUserType('user');
    setAddUserLoading(false);
    setError(null);
    setAddUserForm({
      fullName: '',
      email: '',
      phone: '',
      dateOfBirth: '',
      gender: 'male',
      csaLicence: '',
      emergencyName: '',
      emergencyPhone: '',
      relationship: '',
      allergies: '',
      medicalConditions: '',
      paymentGateway: 'payfast',
      payfastMerchantId: '',
      payfastMerchantKey: '',
      payfastPassphrase: '',
      payfastSandbox: true,
      bankName: '',
      accountHolder: '',
      accountNumber: '',
      branchCode: '',
      accountType: 'Cheque',
      paymentInstructions: ''
    });
  };

  const validateAddUserForm = (): string[] => {
    const errors: string[] = [];
    
    if (!addUserForm.fullName.trim()) errors.push('Full name is required');
    if (!addUserForm.email.trim()) errors.push('Email address is required');
    if (!addUserForm.phone.trim()) errors.push('Phone number is required');
    if (!addUserForm.dateOfBirth) errors.push('Date of birth is required');
    if (!addUserForm.emergencyName.trim()) errors.push('Emergency contact name is required');
    if (!addUserForm.emergencyPhone.trim()) errors.push('Emergency contact phone is required');
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (addUserForm.email && !emailRegex.test(addUserForm.email)) {
      errors.push('Please enter a valid email address');
    }
    
    // Age validation
    if (addUserForm.dateOfBirth) {
      const category = determineMTBCategory(addUserForm.dateOfBirth, addUserForm.gender);
      if (!category.valid) {
        errors.push('Invalid age for MTB categories');
      }
    }
    
    // Coordinator-specific validations
    if (addUserType === 'coordinator') {
      if (addUserForm.paymentGateway === 'payfast' || addUserForm.paymentGateway === 'both') {
        if (!addUserForm.payfastMerchantId.trim()) errors.push('PayFast Merchant ID is required for coordinators');
        if (!addUserForm.payfastMerchantKey.trim()) errors.push('PayFast Merchant Key is required for coordinators');
        if (!addUserForm.payfastPassphrase.trim()) errors.push('PayFast Passphrase is required for coordinators');
      }
      
      if (addUserForm.paymentGateway === 'manual' || addUserForm.paymentGateway === 'both') {
        if (!addUserForm.bankName.trim()) errors.push('Bank name is required for manual payments');
        if (!addUserForm.accountHolder.trim()) errors.push('Account holder is required for manual payments');
        if (!addUserForm.accountNumber.trim()) errors.push('Account number is required for manual payments');
        if (!addUserForm.branchCode.trim()) errors.push('Branch code is required for manual payments');
      }
    }
    
    return errors;
  };

  const handleCreateUser = async () => {
    const validationErrors = validateAddUserForm();
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }
    
    setAddUserLoading(true);
    setError(null);
    
    try {
      // Check if user already exists
      const existingUser = await userService.getUserByEmail(addUserForm.email);
      
      if (existingUser) {
        setError('An account with this email address already exists.');
        setAddUserLoading(false);
        return;
      }

      // Determine rider category
      const category = determineMTBCategory(addUserForm.dateOfBirth, addUserForm.gender);
      
      // Prepare user data
      const userData = {
        email: addUserForm.email,
        name: addUserForm.fullName,
        phone: addUserForm.phone,
        date_of_birth: addUserForm.dateOfBirth,
        gender: addUserForm.gender,
        rider_category: category.category,
        csa_licence: addUserForm.csaLicence || null,
        emergency_contact_name: addUserForm.emergencyName,
        emergency_contact_phone: addUserForm.emergencyPhone,
        emergency_contact_relationship: addUserForm.relationship || null,
        allergies: addUserForm.allergies || 'None',
        medical_conditions: addUserForm.medicalConditions || 'None'
      };

      console.log('Creating user with data:', userData);
      
      // Create user
      const newUser = await userService.createUserFlexible(userData);
      console.log('User created successfully:', newUser);

      // If coordinator, set up coordinator profile and payment config
      if (addUserType === 'coordinator') {
        console.log('Setting up coordinator profile...');
        
        // Set user role to coordinator (bypassing payment)
        const { data: roleUpdate, error: roleError } = await supabase
          .from('users')
          .update({ role: 'coordinator' })
          .eq('id', newUser.id)
          .select()
          .single();

        if (roleError) {
          throw new Error('Failed to set coordinator role');
        }
        
        console.log('Coordinator role set successfully');
        
        // Create coordinator upgrade record (admin-created, no payment required)
        const { data: upgradeRecord, error: upgradeError } = await supabase
          .from('coordinator_upgrades')
          .insert({
            user_id: newUser.id,
            user_email: newUser.email,
            user_name: newUser.name,
            payment_status: 'completed',
            payment_method: 'admin_created',
            payment_reference: `ADMIN_CREATED_${Date.now()}`,
            payment_amount: 0,
            currency: 'ZAR',
            paid_at: new Date().toISOString()
          })
          .select()
          .single();

        if (upgradeError) {
          throw new Error(`Failed to create upgrade record: ${upgradeError.message}`);
        }

        // Create coordinator credits record (1 free credit)
        const { error: creditsError } = await supabase
          .from('coordinator_credits')
          .insert({
            user_id: newUser.id,
            user_email: newUser.email,
            upgrade_id: upgradeRecord.id,
            credits_purchased: 1,
            credits_used: 0,
            credits_remaining: 1
          });

        if (creditsError) {
          throw new Error(`Failed to create credits record: ${creditsError.message}`);
        }
        
        console.log('Coordinator credits created successfully');
      }

      // Reload admin data to show new user
      await loadAdminData();
      
      // Close modal and show success
      handleCloseAddUserModal();
      alert(`${addUserType === 'coordinator' ? 'Coordinator' : 'User'} created successfully!`);
      
    } catch (error: any) {
      console.error('Error creating user:', error);
      setError(error.message || `Failed to create ${addUserType}`);
    } finally {
      setAddUserLoading(false);
    }
  };

  const loadAllParticipants = async () => {
    setParticipantsLoading(true);
    try {
      const { data: participants, error } = await supabase
        .from('registrations')
        .select(`
          *,
          events!inner(title)
        `)
        .order('registration_date', { ascending: false });

      if (error) throw error;

      const formattedParticipants = participants.map(p => ({
        ...p,
        event_title: p.events.title
      }));

      setAllParticipants(formattedParticipants);
      setShowParticipantsModal(true);
    } catch (error: any) {
      console.error('Error loading participants:', error);
      setError('Failed to load participants');
    } finally {
      setParticipantsLoading(false);
    }
  };

  const loadEventParticipants = async (event: Event) => {
    setParticipantsLoading(true);
    setSelectedEventForParticipants(event);
    
    try {
      const { data: participants, error } = await supabase
        .from('registrations')
        .select('*')
        .eq('event_id', event.id)
        .order('registration_date', { ascending: false });

      if (error) throw error;

      const formattedParticipants = participants.map(p => ({
        ...p,
        event_title: event.title
      }));

      setAllParticipants(formattedParticipants);
      setShowParticipantsModal(true);
    } catch (error: any) {
      console.error('Error loading event participants:', error);
      setError('Failed to load event participants');
    } finally {
      setParticipantsLoading(false);
    }
  };

  const handleCloseParticipantsModal = () => {
    setShowParticipantsModal(false);
    setAllParticipants([]);
    setSelectedEventForParticipants(null);
  };

  // CSV Export Functions
  const convertToCSV = (participants: any[], filename: string) => {
    // Define CSV headers
    const headers = [
      'Name',
      'Participant Type',
      'Email', 
      'Phone',
      'ID Number',
      'T-Shirt Size',
      'Event',
      'Distance',
      'Payment Status',
      'Registration Date',
      'Emergency Contact Name',
      'Emergency Contact Phone',
      'Emergency Contact Relationship',
      'Medical Conditions'
    ];

    // Convert participants to CSV rows
    const csvRows = participants.map(participant => {
      // Determine participant type
      let participantType = 'Adult';
      let emergencyContactName = '';
      let emergencyContactPhone = '';
      let emergencyContactRelationship = '';
      
      // Parse emergency contact information
      const emergencyContact = participant.emergency_contact || '';
      
      // Check if this looks like a minor's emergency contact (formatted differently)
      if (emergencyContact.includes('(') && emergencyContact.includes(')')) {
        // This is likely a minor with formatted emergency contact: "Name - Phone (Relationship)"
        participantType = 'Minor';
        
        const match = emergencyContact.match(/^(.*?)\s*-\s*(.*?)\s*\(([^)]+)\)$/);
        if (match) {
          emergencyContactName = match[1].trim();
          emergencyContactPhone = match[2].trim();
          emergencyContactRelationship = match[3].trim();
        } else {
          // Fallback parsing if format is different
          const parts = emergencyContact.split(' - ');
          if (parts.length >= 2) {
            emergencyContactName = parts[0].trim();
            const phoneAndRelation = parts[1].trim();
            const relationMatch = phoneAndRelation.match(/^(.*?)\s*\(([^)]+)\)$/);
            if (relationMatch) {
              emergencyContactPhone = relationMatch[1].trim();
              emergencyContactRelationship = relationMatch[2].trim();
            } else {
              emergencyContactPhone = phoneAndRelation;
            }
          }
        }
      } else {
        // This is likely an adult's emergency contact (simpler format)
        participantType = 'Adult';
        
        if (emergencyContact.includes(' - ')) {
          const parts = emergencyContact.split(' - ');
          emergencyContactName = parts[0].trim();
          emergencyContactPhone = parts[1].trim();
          // For adults, the relationship is often spouse/partner but not always specified
          emergencyContactRelationship = 'Emergency Contact';
        } else {
          // Single field, could be name or phone
          if (emergencyContact.includes('(') || emergencyContact.includes('+') || /\d{3}/.test(emergencyContact)) {
            emergencyContactPhone = emergencyContact;
          } else {
            emergencyContactName = emergencyContact;
          }
        }
      }

      return [
        participant.user_name || '',
        participantType,
        participant.user_email || '',
        participant.user_phone || '',
        participant.id_number || 'Not provided', // Handle missing ID numbers gracefully
        participant.tshirt_size || 'Not specified',
        participant.event_title || '',
        participant.selected_distance || 'Not specified',
        participant.payment_status || 'Unknown',
        participant.registration_date ? formatDate(participant.registration_date) : '',
        emergencyContactName,
        emergencyContactPhone,
        emergencyContactRelationship,
        participant.medical_conditions || ''
      ];
    });

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    return csvContent;
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleExportAllParticipants = () => {
    if (allParticipants.length === 0) {
      alert('No participants to export');
      return;
    }

    const csvContent = convertToCSV(allParticipants, 'all-participants.csv');
    const filename = `gomtb-all-participants-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const handleExportEventParticipants = () => {
    if (allParticipants.length === 0) {
      alert('No participants to export');
      return;
    }

    const csvContent = convertToCSV(allParticipants, 'event-participants.csv');
    const eventName = selectedEventForParticipants?.title.replace(/[^a-zA-Z0-9]/g, '-') || 'event';
    const filename = `gomtb-${eventName}-participants-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  const handleExportSingleParticipant = (participant: any) => {
    const csvContent = convertToCSV([participant], 'participant.csv');
    const participantName = participant.user_name.replace(/[^a-zA-Z0-9]/g, '-') || 'participant';
    const filename = `gomtb-${participantName}-${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faSpinner} className="text-4xl text-primary animate-spin mb-4" />
        <p className="text-gray-600">Loading admin panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen px-4">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
        <h2 className="text-xl font-semibold mb-4">Error Loading Admin Panel</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <button 
          onClick={loadAdminData}
          className="bg-primary text-white px-6 py-2 rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 font-sans text-dark">
      {/* Header */}
      <header className="app-header">
        <div className="flex items-center justify-between">
          <Link to="/dashboard" className="app-header-back">
            <FontAwesomeIcon icon={faArrowLeft} className="text-lg" />
          </Link>
          <h1 className="app-header-title">Admin Panel</h1>
          <button 
            onClick={loadAdminData}
            className="app-header-action"
          >
            <FontAwesomeIcon icon={faRefresh} className="text-lg" />
          </button>
        </div>
      </header>

      {/* Tab Navigation */}
      <nav className="app-tab-nav">
        <div className="flex overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: faChartLine },
            { id: 'users', label: 'Users', icon: faUsers },
            { id: 'events', label: 'Events', icon: faCalendarDays },
            { id: 'system', label: 'System', icon: faGear }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-400 hover:text-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} className="mr-2" />
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="content-container pb-20">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-4 p-4">
            {/* Key Metrics */}
            <section className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">System Overview</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
                  <div className="text-orange-500 mb-1">
                    <FontAwesomeIcon icon={faUsers} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Total Users</p>
                  <h4 className="text-xl font-bold">{adminStats?.totalUsers || 0}</h4>
                </div>
                <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                  <div className="text-green-500 mb-1">
                    <FontAwesomeIcon icon={faCalendarDays} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Total Events</p>
                  <h4 className="text-xl font-bold">{adminStats?.totalEvents || 0}</h4>
                </div>
                <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                  <div className="text-purple-500 mb-1">
                    <FontAwesomeIcon icon={faChild} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Registered Minors</p>
                  <h4 className="text-xl font-bold">{adminStats?.registeredMinors || 0}</h4>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
                  <div className="text-amber-500 mb-1">
                    <FontAwesomeIcon icon={faUserTie} className="text-xl" />
                  </div>
                  <p className="text-xs text-gray-600">Coordinators</p>
                  <h4 className="text-xl font-bold">{adminStats?.coordinators || 0}</h4>
                </div>
              </div>
            </section>

            {/* This Month Stats */}
            <section className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">This Month</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{adminStats?.systemHealth.eventsThisMonth || 0}</p>
                  <p className="text-xs text-gray-600">New Events</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{adminStats?.systemHealth.registrationsThisMonth || 0}</p>
                  <p className="text-xs text-gray-600">Registrations</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600">{adminStats?.systemHealth.activeUsers || 0}</p>
                  <p className="text-xs text-gray-600">Active Users</p>
                </div>
              </div>
            </section>

            {/* Recent Activity */}
            <section className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">Recent Activity</h3>
              <div className="space-y-3">
                {adminStats?.recentActivity && adminStats.recentActivity.length > 0 ? (
                  adminStats.recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        activity.type === 'registration' ? 'bg-green-100 text-green-600' :
                        activity.type === 'event' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        <FontAwesomeIcon icon={
                          activity.type === 'registration' ? faUsers :
                          activity.type === 'event' ? faCalendarDays :
                          faBell
                        } className="text-sm" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{activity.description}</p>
                        <p className="text-xs text-gray-500">
                          {activity.user} • {getTimeAgo(activity.timestamp)}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-4">No recent activity</p>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4 p-4">
            {/* Add User Buttons */}
            <section className="bg-white rounded-lg p-4">
              <div className="flex space-x-3 mb-4">
                <button 
                  onClick={() => {
                    setAddUserType('user');
                    setShowAddUserModal(true);
                  }}
                  className="flex-1 bg-primary text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-orange-600 transition-colors"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-lg" />
                  <span className="font-medium">Add User</span>
                </button>
                <button 
                  onClick={() => {
                    setAddUserType('coordinator');
                    setShowAddUserModal(true);
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-green-700 transition-colors"
                >
                  <FontAwesomeIcon icon={faUserTie} className="text-lg" />
                  <span className="font-medium">Add Coordinator</span>
                </button>
              </div>
              <p className="text-xs text-gray-500 text-center">
                Add users directly or create coordinators with event creation credits (bypasses PayFast payment)
              </p>
            </section>

            {/* Search */}
            <section className="bg-white rounded-lg p-4">
              <div className="relative">
                <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                />
              </div>
            </section>

            {/* Users List */}
            <section className="bg-white rounded-lg">
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-gray-800">All Users ({filteredUsers.length})</h3>
                  <button className="text-gray-500 hover:text-primary">
                    <FontAwesomeIcon icon={faFilter} />
                  </button>
                </div>
              </div>
              <div className="divide-y">
              {filteredUsers.map((user) => (
                  <div key={user.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200">
                        <img 
                          src={user.profile_image_url || "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"} 
                          alt={user.name}
                          className="w-full h-full object-cover" 
                        />
                      </div>
                      <div>
                        <h4 className="font-medium">{user.name}</h4>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            user.status === 'active' ? 'bg-green-100 text-green-800' :
                            user.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {user.status}
                          </span>
                          <span className="text-xs text-gray-500">
                            {user.registrationCount} registrations
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <button 
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserDetails(true);
                        }}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="View Details"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </button>
                      <button 
                        className="p-2 text-gray-500 hover:text-primary"
                        title="Edit User"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        className="p-2 text-gray-500 hover:text-red-500"
                        title="Suspend User"
                      >
                        <FontAwesomeIcon icon={faShield} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 text-xs text-gray-500">
                    Last activity: {getTimeAgo(user.lastActivity)} • 
                    Joined: {formatDate(user.created_at)}
                  </div>
                </div>
              ))}
                {filteredUsers.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No users found matching your search.
                  </div>
                )}
              </div>
            </section>
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-4 p-4">
            {/* Add Event Button */}
            <section className="bg-white rounded-lg p-4">
              <Link 
                to="/coordinator/events" 
                className="w-full bg-primary text-white px-4 py-3 rounded-lg flex items-center justify-center space-x-2 hover:bg-orange-600 transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-lg" />
                <span className="font-medium">Add New Event (Admin)</span>
              </Link>
              <p className="text-xs text-gray-500 mt-2 text-center">
                As an admin, you have unlimited event creation credits
              </p>
            </section>

            {/* Search and Actions */}
            <section className="bg-white rounded-lg p-4">
              <div className="flex space-x-3 mb-3">
                <div className="flex-1 relative">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search events..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>
                <button 
                  onClick={loadAllParticipants}
                  disabled={participantsLoading}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center"
                >
                  {participantsLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  ) : (
                    <FontAwesomeIcon icon={faUsers} className="mr-2" />
                  )}
                  All Participants
                </button>
              </div>
            </section>

            {/* Events List */}
            <section className="space-y-3">
              {filteredEvents.map((event) => (
                <div key={event.id} className="bg-white rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-medium">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.location}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(event.date)} • {event.max_participants} max participants
                      </p>
                    </div>
                    <div className="flex space-x-1 ml-4">
                      <Link 
                        to={`/events/${event.id}`}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="View Event"
                      >
                        <FontAwesomeIcon icon={faEye} />
                      </Link>
                      <button 
                        onClick={() => {
                          console.log('Edit event clicked:', event.id);
                          initializeEditForm(event);
                        }}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="Edit Event"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button 
                        onClick={() => loadEventParticipants(event)}
                        className="p-2 text-gray-500 hover:text-primary"
                        title="View Participants"
                      >
                        <FontAwesomeIcon icon={faUsers} />
                      </button>
                      <button 
                        className="p-2 text-gray-500 hover:text-red-500"
                        title="Delete Event"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {filteredEvents.length === 0 && (
                <div className="bg-white rounded-lg p-8 text-center text-gray-500">
                  No events found matching your search.
                </div>
              )}
            </section>
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && (
          <div className="space-y-4 p-4">
            {/* System Health */}
            <section className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">System Health</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faDatabase} className="text-green-600 mr-3" />
                    <span className="font-medium">Database</span>
                  </div>
                  <span className="text-green-600 font-medium">Healthy</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faShield} className="text-green-600 mr-3" />
                    <span className="font-medium">Security</span>
                  </div>
                  <span className="text-green-600 font-medium">Secure</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <FontAwesomeIcon icon={faGear} className="text-green-600 mr-3" />
                    <span className="font-medium">API Status</span>
                  </div>
                  <span className="text-green-600 font-medium">Online</span>
                </div>
              </div>
            </section>

            {/* Quick Actions */}
            <section className="bg-white rounded-lg p-4">
              <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-2 gap-3">
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center">
                  <FontAwesomeIcon icon={faDownload} className="text-primary text-xl mb-2" />
                  <p className="text-sm font-medium">Export Data</p>
                </button>
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center">
                  <FontAwesomeIcon icon={faRefresh} className="text-primary text-xl mb-2" />
                  <p className="text-sm font-medium">Refresh Cache</p>
                </button>
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center">
                  <FontAwesomeIcon icon={faGear} className="text-primary text-xl mb-2" />
                  <p className="text-sm font-medium">System Settings</p>
                </button>
                <button className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 text-center">
                  <FontAwesomeIcon icon={faShield} className="text-primary text-xl mb-2" />
                  <p className="text-sm font-medium">Security Logs</p>
                </button>
              </div>
            </section>
          </div>
        )}
      </main>

      {/* Add User/Coordinator Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  Add {addUserType === 'coordinator' ? 'Coordinator' : 'User'}
                </h2>
                <button 
                  onClick={handleCloseAddUserModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Basic Information */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                      <input
                        type="text"
                    value={addUserForm.fullName}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, fullName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter full name"
                      />
                    </div>

                    <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input
                    type="email"
                    value={addUserForm.email}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter email address"
                      />
                    </div>

                      <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                        <input
                    type="tel"
                    value={addUserForm.phone}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter phone number"
                        />
                      </div>

                      <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth *</label>
                        <input
                    type="date"
                    value={addUserForm.dateOfBirth}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, dateOfBirth: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender *</label>
                  <select
                    value={addUserForm.gender}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, gender: e.target.value as 'male' | 'female' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                    </div>

                    <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Name *</label>
                      <input
                        type="text"
                    value={addUserForm.emergencyName}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, emergencyName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Emergency contact name"
                      />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Emergency Contact Phone *</label>
                        <input
                    type="tel"
                    value={addUserForm.emergencyPhone}
                    onChange={(e) => setAddUserForm(prev => ({ ...prev, emergencyPhone: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Emergency contact phone"
                        />
                      </div>

                {/* Coordinator-specific fields */}
                {addUserType === 'coordinator' && (
                  <div className="border-t pt-4">
                    <h3 className="font-medium text-gray-900 mb-3">Coordinator Settings</h3>

                      <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Payment Gateway</label>
                        <select
                        value={addUserForm.paymentGateway}
                        onChange={(e) => setAddUserForm(prev => ({ ...prev, paymentGateway: e.target.value as 'payfast' | 'manual' | 'both' }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        >
                        <option value="payfast">PayFast Only</option>
                        <option value="manual">Manual Only</option>
                        <option value="both">Both PayFast & Manual</option>
                        </select>
                    </div>

                    {(addUserForm.paymentGateway === 'payfast' || addUserForm.paymentGateway === 'both') && (
                      <div className="space-y-3 mt-3">
                        <h4 className="font-medium text-gray-800">PayFast Configuration</h4>
                        <input
                          type="text"
                          placeholder="PayFast Merchant ID *"
                          value={addUserForm.payfastMerchantId}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, payfastMerchantId: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                        <input
                          type="text"
                          placeholder="PayFast Merchant Key *"
                          value={addUserForm.payfastMerchantKey}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, payfastMerchantKey: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                      <input
                          type="text"
                          placeholder="PayFast Passphrase *"
                          value={addUserForm.payfastPassphrase}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, payfastPassphrase: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                        </div>
                      )}

                    {(addUserForm.paymentGateway === 'manual' || addUserForm.paymentGateway === 'both') && (
                      <div className="space-y-3 mt-3">
                        <h4 className="font-medium text-gray-800">Bank Details</h4>
                      <input
                          type="text"
                          placeholder="Bank Name *"
                          value={addUserForm.bankName}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, bankName: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                      />
                            <input
                              type="text"
                          placeholder="Account Holder *"
                          value={addUserForm.accountHolder}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, accountHolder: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                            <input
                              type="text"
                          placeholder="Account Number *"
                          value={addUserForm.accountNumber}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, accountNumber: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                        />
                          <input
                            type="text"
                          placeholder="Branch Code *"
                          value={addUserForm.branchCode}
                          onChange={(e) => setAddUserForm(prev => ({ ...prev, branchCode: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                          />
                        </div>
                        )}
                      </div>
                )}
                </div>

              <div className="flex space-x-3 mt-6">
                <button 
                  onClick={handleCloseAddUserModal}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreateUser}
                  disabled={addUserLoading}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {addUserLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  ) : null}
                  Create {addUserType === 'coordinator' ? 'Coordinator' : 'User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">
                  {selectedEventForParticipants 
                    ? `Participants - ${selectedEventForParticipants.title}` 
                    : 'All Participants'
                  }
                </h2>
                <button 
                  onClick={handleCloseParticipantsModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>

              <div className="mb-4 flex justify-between items-center">
                <p className="text-sm text-gray-600">
                  Total participants: {allParticipants.length}
                </p>
                <button
                  onClick={selectedEventForParticipants ? handleExportEventParticipants : handleExportAllParticipants}
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center space-x-2"
                  disabled={allParticipants.length === 0}
                >
                  <FontAwesomeIcon icon={faDownload} />
                  <span>Export All to CSV</span>
                </button>
            </div>
            
                <div className="space-y-3">
                {allParticipants.map((participant) => (
                  <div key={participant.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-medium">{participant.user_name}</h4>
                          <p className="text-sm text-gray-600">{participant.user_email}</p>
                        <p className="text-sm text-gray-500">{participant.event_title}</p>
                        <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                          <span>Distance: {(participant as any).selected_distance || 'Not specified'}</span>
                          <span>Status: {(participant as any).payment_status || 'Unknown'}</span>
                          <span>Registered: {formatDate((participant as any).registration_date)}</span>
                        </div>
                      </div>
                      <div className="flex space-x-1">
                        <button 
                          className="p-2 text-gray-500 hover:text-primary"
                          title="View Details"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button 
                          onClick={() => handleExportSingleParticipant(participant)}
                          className="p-2 text-gray-500 hover:text-primary"
                          title="Download CSV"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </button>
                        </div>
                      </div>
                    </div>
                  ))}
                {allParticipants.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No participants found.
                </div>
              )}
            </div>
            
              <div className="flex justify-end mt-6">
                <button 
                  onClick={handleCloseParticipantsModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserDetails && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">User Details</h2>
                <button 
                  onClick={() => setShowUserDetails(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            
              <div className="space-y-4">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-200">
                    <img 
                      src={selectedUser.profile_image_url || "https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-3.jpg"} 
                      alt={selectedUser.name}
                      className="w-full h-full object-cover" 
                      />
                    </div>
                    <div>
                    <h3 className="font-bold text-lg">{selectedUser.name}</h3>
                    <p className="text-gray-600">{selectedUser.email}</p>
                    <p className="text-sm text-gray-500">
                      Joined: {formatDate(selectedUser.created_at)}
                    </p>
                    </div>
                    </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <p className="text-gray-900">{selectedUser.phone || 'Not provided'}</p>
                    </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                      selectedUser.status === 'active' ? 'bg-green-100 text-green-800' :
                      selectedUser.status === 'inactive' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {selectedUser.status}
                    </span>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                    <p className="text-gray-900">
                      {selectedUser.date_of_birth ? formatDate(selectedUser.date_of_birth) : 'Not provided'}
                    </p>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Gender</label>
                    <p className="text-gray-900 capitalize">{selectedUser.gender || 'Not specified'}</p>
                    </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Rider Category</label>
                    <p className="text-gray-900">{selectedUser.rider_category || 'Not determined'}</p>
                    </div>
                    <div>
                    <label className="block text-sm font-medium text-gray-700">Registrations</label>
                    <p className="text-gray-900">{selectedUser.registrationCount}</p>
                  </div>
                </div>

                  <div>
                  <label className="block text-sm font-medium text-gray-700">Emergency Contact</label>
                  <p className="text-gray-900">
                    {selectedUser.emergency_contact_name || 'Not provided'}
                    {selectedUser.emergency_contact_phone && ` - ${selectedUser.emergency_contact_phone}`}
                  </p>
                    </div>

                      <div>
                  <label className="block text-sm font-medium text-gray-700">Medical Information</label>
                  <p className="text-gray-900 text-sm">
                    <strong>Allergies:</strong> {selectedUser.allergies || 'None'}<br />
                    <strong>Medical Conditions:</strong> {selectedUser.medical_conditions || 'None'}
                  </p>
                        </div>
                      </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button 
                  onClick={() => setShowUserDetails(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button 
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-orange-600"
                >
                  Edit User
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Event Modal */}
      {showEditModal && editingEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Edit Event</h2>
                <button 
                  onClick={handleCloseEditModal}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
              
              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {/* Event Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="datetime-local"
                    value={editForm.date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location *</label>
                  <input
                    type="text"
                    value={editForm.location}
                    onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event location"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Participants *</label>
                  <input
                    type="number"
                    value={editForm.max_participants}
                    onChange={(e) => setEditForm(prev => ({ ...prev, max_participants: Number(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Registration Deadline *</label>
                  <input
                    type="datetime-local"
                    value={editForm.registration_deadline}
                    onChange={(e) => setEditForm(prev => ({ ...prev, registration_deadline: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Requirements</label>
                  <textarea
                    value={editForm.requirements}
                    onChange={(e) => setEditForm(prev => ({ ...prev, requirements: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event requirements"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                  <input
                    type="text"
                    value={editForm.amenities.join(', ')}
                    onChange={(e) => setEditForm(prev => ({ ...prev, amenities: e.target.value.split(',').map(a => a.trim()) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event amenities (comma-separated)"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Organizer *</label>
                  <input
                    type="text"
                    value={editForm.organizer}
                    onChange={(e) => setEditForm(prev => ({ ...prev, organizer: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event organizer"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Difficulty</label>
                  <select
                    value={editForm.difficulty}
                    onChange={(e) => setEditForm(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Entry Fee From</label>
                  <input
                    type="text"
                    value={editForm.entry_fee_from}
                    onChange={(e) => setEditForm(prev => ({ ...prev, entry_fee_from: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter entry fee from"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
                  <input
                    type="text"
                    value={editForm.image_url}
                    onChange={(e) => setEditForm(prev => ({ ...prev, image_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event image URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Route URL</label>
                  <input
                    type="text"
                    value={editForm.route_url}
                    onChange={(e) => setEditForm(prev => ({ ...prev, route_url: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                    placeholder="Enter event route URL"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Goreg</label>
                  <select
                    value={editForm.goreg}
                    onChange={(e) => setEditForm(prev => ({ ...prev, goreg: e.target.value as 'yes' | 'no' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>

                {/* Distances Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Distances</label>
                  {editForm.distances.map((distance, idx) => (
                    <div key={distance.id} className="mb-4 p-3 border rounded-lg bg-gray-50">
                      <div className="flex flex-wrap gap-2 mb-2">
                        <input
                          type="text"
                          value={distance.name}
                          onChange={e => setEditForm(prev => {
                            const updated = [...prev.distances];
                            updated[idx].name = e.target.value;
                            return { ...prev, distances: updated };
                          })}
                          className="flex-1 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Name"
                        />
                        <input
                          type="text"
                          value={distance.length}
                          onChange={e => setEditForm(prev => {
                            const updated = [...prev.distances];
                            updated[idx].length = e.target.value;
                            return { ...prev, distances: updated };
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Length"
                        />
                        <input
                          type="text"
                          value={distance.price}
                          onChange={e => setEditForm(prev => {
                            const updated = [...prev.distances];
                            updated[idx].price = e.target.value;
                            return { ...prev, distances: updated };
                          })}
                          className="w-20 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Price"
                        />
                        <input
                          type="text"
                          value={distance.ageRestriction}
                          onChange={e => setEditForm(prev => {
                            const updated = [...prev.distances];
                            updated[idx].ageRestriction = e.target.value;
                            return { ...prev, distances: updated };
                          })}
                          className="w-24 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Age Restriction"
                        />
                        <input
                          type="text"
                          value={distance.difficulty}
                          onChange={e => setEditForm(prev => {
                            const updated = [...prev.distances];
                            updated[idx].difficulty = e.target.value;
                            return { ...prev, distances: updated };
                          })}
                          className="w-28 px-2 py-1 border border-gray-300 rounded"
                          placeholder="Difficulty"
                        />
                        <button
                          type="button"
                          onClick={() => setEditForm(prev => ({
                            ...prev,
                            distances: prev.distances.filter((_, i) => i !== idx)
                          }))}
                          className="px-2 py-1 text-red-600 hover:text-red-800"
                          title="Remove Distance"
                        >
                          Remove
                        </button>
                      </div>
                      <input
                        type="text"
                        value={distance.description}
                        onChange={e => setEditForm(prev => {
                          const updated = [...prev.distances];
                          updated[idx].description = e.target.value;
                          return { ...prev, distances: updated };
                        })}
                        className="w-full px-2 py-1 border border-gray-300 rounded"
                        placeholder="Description"
                      />
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setEditForm(prev => ({
                      ...prev,
                      distances: [
                        ...prev.distances,
                        {
                          id: (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now())),
                          name: '',
                          length: '',
                          price: '',
                          description: '',
                          ageRestriction: '',
                          difficulty: ''
                        }
                      ]
                    }))}
                    className="mt-2 px-4 py-2 bg-primary text-white rounded hover:bg-orange-600"
                  >
                    Add Distance
                  </button>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button 
                  onClick={handleCloseEditModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleUpdateEvent}
                  disabled={editLoading}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-orange-600 disabled:opacity-50"
                >
                  {editLoading ? (
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  ) : null}
                  Update Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminManagement;